import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useSite } from "@/context/SiteContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, waLink } from "@/lib/helpers";
import { Seo } from "@/components/site/Seo";
import { FloatingMotifs, FairySil } from "@/components/site/Motifs";
import { WhatsAppIcon } from "@/components/site/WhatsAppFab";
import { StepOne } from "@/components/booking/StepOne";
import { StepTwo, normalizePhone } from "@/components/booking/StepTwo";

function Success({ result, site }) {
  const text = `Merhaba, ${result.request_no} numaralı tanışma dersi talebim hakkında yazıyorum (${result.course_name}, ${fmtDate(result.date)} ${result.time}).`;
  return (
    <div className="text-center py-6" data-testid="booking-success">
      <div className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-bej"><CheckCircle2 className="w-10 h-10 text-navy" /></div>
      <h2 className="mt-6 font-heading text-3xl font-black text-navy">Talebiniz alındı!</h2>
      <p className="mt-3 text-slate-600">En kısa sürede sizi arayarak ya da WhatsApp'tan yazarak randevunuzu onaylayacağız.</p>
      <div className="mx-auto mt-8 max-w-sm rounded-3xl border-2 border-dashed border-navy/30 bg-bej-light p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-navy/60">Talep numaranız</p>
        <p className="mt-2 font-heading text-3xl font-black text-navy tracking-wide" data-testid="booking-request-no">{result.request_no}</p>
        <p className="mt-3 text-sm text-navy/80">{result.course_name} · {fmtDate(result.date)} · {result.time}</p>
      </div>
      {site?.whatsapp_number && (
        <a href={waLink(site.whatsapp_number, text)} target="_blank" rel="noreferrer" data-testid="booking-success-whatsapp"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-heading font-bold text-white hover:brightness-95 transition-[filter]">
          <WhatsAppIcon className="w-5 h-5" /> WhatsApp'tan yaz
        </a>
      )}
    </div>
  );
}

const initial = (kurs) => ({ course_id: kurs || "", date: "", time: "", name: "", phone: "", student_type: "cocuk",
  student_age: "", note: "", kvkk_consent: false, website: "" });

function validate(f) {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Lütfen ad soyad girin.";
  if (!normalizePhone(f.phone)) e.phone = "Geçerli bir cep telefonu girin (5XX XXX XX XX).";
  const age = Number(f.student_age);
  if (!age || age < 3 || age > 99) e.student_age = "Yaş 3 ile 99 arasında olmalı.";
  if (!f.kvkk_consent) e.kvkk_consent = "Devam etmek için KVKK açık rıza onayı zorunludur.";
  return e;
}

export default function Booking() {
  const [params] = useSearchParams();
  const { site } = useSite();
  const { data: courses } = useFetch("/courses");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => initial(params.get("kurs")));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", { ...form, phone: normalizePhone(form.phone), student_age: Number(form.student_age) });
      setResult(data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setErrors({ form: errMsg(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Randevu Al" description="Masal Sanat Akademi tanışma dersi randevu talebi. Kursunuzu, gününüzü ve saatinizi seçin; iki adımda talebinizi bırakın." />
      <section className="relative overflow-hidden bg-bej-light paper-grain">
        <FloatingMotifs />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 pt-10 pb-28 sm:py-16">
          <div className="flex items-center gap-4 mb-8">
            <FairySil className="w-14 h-14 text-navy shrink-0" />
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black text-navy" data-testid="page-title">Tanışma dersi randevusu</h1>
              <p className="text-slate-600 mt-1">Hesap açmanıza gerek yok. İki kısa adım.</p>
            </div>
          </div>
          {step < 3 && (
            <div className="flex gap-2 mb-6" data-testid="booking-steps">
              {["Kurs, gün ve saat", "Bilgileriniz"].map((l, i) => (
                <div key={l} className={`flex-1 rounded-full px-4 py-2 text-center text-sm font-bold ${step === i + 1 ? "bg-navy text-white" : step > i + 1 ? "bg-bej text-navy" : "bg-white text-navy/50"}`}>
                  {i + 1}. {l}
                </div>
              ))}
            </div>
          )}
          <div className="rounded-[2rem] bg-white p-5 sm:p-10 shadow-xl shadow-navy/5 border border-bej">
            {step === 1 && <StepOne courses={courses || []} site={site} form={form} set={set} onNext={() => setStep(2)} />}
            {step === 2 && <StepTwo form={form} set={set} errors={errors} submitting={submitting} onBack={() => setStep(1)} onSubmit={submit} />}
            {step === 3 && result && <Success result={result} site={site} />}
          </div>
        </div>
      </section>
    </>
  );
}
